import { CMSMedia, CMSMediaType } from '@/cms/fields/mappers/productDetailPageFieldsMapper'
import { useIntl } from '@/intl'
import { LazyImage } from '@/scopes/common/components/Image'
import * as Dialog from '@radix-ui/react-dialog'
import { mqMin, space, tok, zIndex } from '@sunwebgroup/ui-components'
import { Button, IconButton, TopTitleBar } from '@sunwebgroup/ui-components/uds'
import useEmblaCarousel from 'embla-carousel-react'
import * as React from 'react'
import { PropsWithChildren, useCallback, useEffect, useState } from 'react'
import { css, styled } from 'styled-components'
import { Video } from '../HeroSection/Gallery/Video'

export type DetailProps = PropsWithChildren<Dialog.DialogProps> & {
  startFromIndex?: number
  onClose?: () => void
  media: CMSMedia[]
  onSettle: () => void
}

const ImageVideoWrapper = styled.div.attrs<{ $visible: boolean }>(props => ({ $visible: props.$visible }))`
  display: ${({ $visible }) => ($visible ? 'block' : 'none')};
  height: 100%;
  width: 100%;
  position: relative;

  img {
    transition: all 0.3s ease-in-out;
    width: 100%;
    display: block;
  }

  &:hover {
    img {
      filter: brightness(0.85);
    }
  }
`

const ButtonOverlay = styled.button`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: transparent;
  z-index: 1;
`

const StyledVideo = styled(Video).attrs<{ $visible: boolean }>(props => ({
  $visible: props.$visible,
}))`
  ${({ $visible }) =>
    $visible
      ? css`
          display: block;
        `
      : css`
          display: none;
        `};
`

const Header = styled(TopTitleBar.Root)`
  background: ${tok('primitives_color_neutral_1000')};
  color: ${tok('semantics_content_white_color')};
  border: unset;
`

const Wrapper = styled.div`
  background: ${tok('primitives_color_neutral_1000')};
  z-index: ${zIndex.level6 + 50} !important;
  inset: 0px;
  display: flex;
  flex-direction: column;
  position: fixed;
`

const Body = styled.div`
  flex: 1 1 0%;
  overflow-y: auto;

  @media ${mqMin('primitives_breakpoint_m_min')} {
    padding: 0 ${space('primitives_space_40')};
  }

  @media ${mqMin('primitives_breakpoint_l_min')} {
    padding: 0 ${space('primitives_space_80')};
  }
`

const Footer = styled.div`
  min-height: ${space('primitives_space_48')};
`

const NavButton = styled(IconButton)`
  display: none;
  position: absolute;
  top: 50%;
  transform: translateY(-50%);

  @media ${mqMin('primitives_breakpoint_l_min')} {
    display: inline-flex;
  }
`

const PlayIconButton = styled(IconButton)`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
`

const LeftButton = styled(NavButton)`
  left: ${space('primitives_space_16')};
`

const RightButton = styled(NavButton)`
  right: ${space('primitives_space_16')};
`

const Container = styled.section`
  position: relative;
  height: 100%;
`

const Slider = styled.div`
  overflow: hidden;
  height: 100%;
`

const SliderContainer = styled.div`
  display: flex;
  height: 100%;
`

const Slide = styled.div`
  flex: 0 0 100%;

  iframe {
    height: 100%;
    width: 100%;
  }

  img {
    object-fit: contain;
    height: 100%;
    background: ${tok('primitives_color_neutral_1000')};
  }
`

const CloseButton = styled(Button)`
  &:not(disabled) {
    color: ${tok('semantics_content_white_color')};
  }

  &:hover {
    color: ${tok('semantics_content_white_color')};
  }
`

export function FullScreenGallery({ startFromIndex = 0, onClose, media, onSettle, ...attrs }: DetailProps) {
  const intl = useIntl()
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: false, startIndex: startFromIndex })
  const [slidesInView, setSlidesInView] = useState<number[]>([])
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0)
  const [activeVideo, setActiveVideo] = useState(false)
  const numberOfSlides = media ? media.length : 0

  const findSlidesInView = useCallback(() => {
    if (!emblaApi)
      return

    setSlidesInView((slidesInViewElements) => {
      if (slidesInViewElements.length === emblaApi.slideNodes().length) {
        emblaApi.off('slidesInView', findSlidesInView)
      }
      const inView = emblaApi.slidesInView().filter(index => !slidesInViewElements.includes(index))
      return slidesInViewElements.concat(inView)
    })
  }, [emblaApi, setSlidesInView])

  function navigate(direction: 'previous' | 'next') {
    if (direction === 'previous') {
      emblaApi?.scrollPrev()
    }
    else {
      emblaApi?.scrollNext()
    }
  }

  // Slider events
  const onSelect = useCallback(() => {
    if (emblaApi !== undefined) {
      const index = emblaApi.selectedScrollSnap()
      setCurrentSlideIndex(index)
    }
  }, [emblaApi])

  const onScroll = useCallback(() => {
    setActiveVideo(false)
  }, [])

  const onSettleCallback = useCallback(() => {
    onSettle()
  }, [])

  const showVideo = () => {
    setActiveVideo(true)
  }

  useEffect(() => {
    if (emblaApi === undefined)
      return

    setCurrentSlideIndex(emblaApi.selectedScrollSnap())
    emblaApi.on('select', onSelect)
    emblaApi.on('scroll', onScroll)
    emblaApi.on('settle', onSettleCallback)
    emblaApi.on('slidesInView', findSlidesInView)
    emblaApi.reInit()

    return () => {
      emblaApi.off('select', onSelect)
      emblaApi.off('scroll', onScroll)
      emblaApi.off('settle', onSettle)
      emblaApi.off('slidesInView', findSlidesInView)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Only run once embla is mounted
  }, [emblaApi])

  const handlePauseOrEnded = () => {
    setActiveVideo(false)
  }

  const handleClose = () => {
    setActiveVideo(false)
    if (onClose) {
      onClose()
    }
  }

  return (

    <Dialog.Root {...attrs} onOpenChange={handleClose}>
      <Dialog.Portal>
        <Dialog.Content asChild>
          <Wrapper>
            <Header>
              <TopTitleBar.CenterArea title={`${currentSlideIndex + 1}  / ${numberOfSlides}`} />
              <TopTitleBar.RightArea>
                <Dialog.Close asChild>
                  <CloseButton $type="transparentPrimary">{intl.formatMessage({ id: 'close' })}</CloseButton>
                </Dialog.Close>
              </TopTitleBar.RightArea>
            </Header>
            <Body>
              <Container>
                <Slider ref={emblaRef}>
                  <SliderContainer>
                    {media?.map((mediaItem, index) => {
                      if (mediaItem.type === CMSMediaType.VIDEO) {
                        return (
                          <Slide key={index}>
                            <StyledVideo
                              link={mediaItem.link || ''}
                              playing={activeVideo}
                              onPauseOrEnded={handlePauseOrEnded}
                              $visible={activeVideo}
                            />
                            <ImageVideoWrapper onClick={showVideo} $visible={!activeVideo}>
                              <ButtonOverlay onClick={showVideo} />
                              <LazyImage src={mediaItem.src} srcSet={[1280, 480]} alt={mediaItem.alt || ''} loading={!slidesInView.includes(index) ? 'lazy' : 'eager'} />
                              <PlayIconButton
                                glyph="PlayFilled"
                                type="overlay-neutral"
                                ariaLabel="Play video"
                                size="M"
                              />
                            </ImageVideoWrapper>
                          </Slide>
                        )
                      }

                      return (
                        <Slide key={index}>
                          <LazyImage src={mediaItem.src} srcSet={[1280, 480]} alt={mediaItem.alt || ''} loading={!slidesInView.includes(index) ? 'lazy' : 'eager'} />
                        </Slide>
                      )
                    })}
                  </SliderContainer>
                </Slider>
              </Container>
              {numberOfSlides > 0 && (
                <>
                  <LeftButton
                    type="secondary"
                    onClick={() => navigate('previous')}
                    disabled={currentSlideIndex === 0}
                    glyph="ChevronLeft"
                  />
                  <RightButton
                    type="secondary"
                    onClick={() => navigate('next')}
                    disabled={currentSlideIndex === numberOfSlides - 1}
                    glyph="ChevronRight"
                  />
                </>
              )}
            </Body>
            <Footer />
          </Wrapper>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
